/**
 * WaveChart — minimal canvas streaming line chart.
 *
 * Drawn imperatively from the telemetry subscription (never via React state),
 * one full redraw per batch (~2/sec). Canvas keeps this cheap enough for
 * dozens of cards.
 */
import { isDemo } from './telemetryStore';

export default class WaveChart {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.yMin = null;
    this.yMax = null;
    // Vertical zoom (display gain). 1 = the mobile's ±2.6818 window; the owning
    // card sets this to 0.5 / 1 / 2 to fit small- or large-amplitude patients.
    this.zoom = 1;
    this.resize();
  }

  // Drop the smoothed y-scale so the next draw snaps to the fresh data
  // (used on a lead-on wipe).
  reset() {
    this.yMin = null;
    this.yMax = null;
  }

  resize() {
    const dpr = window.devicePixelRatio || 1;
    const { clientWidth: w, clientHeight: h } = this.canvas;
    if (!w || !h) return;
    this.canvas.width = Math.round(w * dpr);
    this.canvas.height = Math.round(h * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.w = w;
    this.h = h;
  }

  /**
   * @param buf      ring buffer from telemetryStore ({times, values, head, size, cap})
   * @param windowMs visible time window
   * @param now      right edge timestamp
   * @param color    line color (blue normally, grey when stale)
   */
  draw(buf, windowMs, now, color) {
    const { ctx, w, h } = this;
    if (!w || !h || !buf || buf.size === 0) return;

    const t0 = now - windowMs;

    // Fixed ECG scale (matches the mobile app). Raw device counts arrive as v;
    // dividing by ECG_SCALE converts to the mobile's mV-ish unit, then the plot
    // is locked to [ECG_Y_MIN, ECG_Y_MAX] instead of auto-scaling — so the trace
    // stays a steady size. A beat beyond the range simply runs off the top/
    // bottom (the canvas clips it) rather than being flattened into a constant
    // line, so real spikes stay visible on the review screen.
    // Tune these three constants if the trace sits too big or too small.
    // Real device data is raw ADC counts (needs ÷1000 to reach the mobile's
    // mV-ish unit); demo data is already synthesised in mV, so it divides by 1.
    const ECG_SCALE = isDemo() ? 1 : 1000;
    const ECG_Y_MIN = -2.6818;
    const ECG_Y_MAX =  2.6818;

    // Nothing to draw if no sample falls inside the visible window.
    const start = (buf.head - buf.size + buf.cap) % buf.cap;
    let hasData = false;
    for (let i = 0; i < buf.size; i++) {
      if (buf.times[(start + i) % buf.cap] >= t0) { hasData = true; break; }
    }
    if (!hasData) return;

    // Apply the card's vertical zoom (gain). Dividing the range by the gain
    // scales the trace height without moving its centre: 2x halves the visible
    // range so a small-amplitude trace is drawn twice as tall; 0.5x doubles the
    // range so a large-amplitude (e.g. infant) trace fits. Data is untouched.
    const gain = this.zoom || 1;
    this.yMin = ECG_Y_MIN / gain;
    this.yMax = ECG_Y_MAX / gain;

    const x = t => ((t - t0) / windowMs) * w;
    const y = v => h - (((v / ECG_SCALE) - this.yMin) / (this.yMax - this.yMin)) * h;

    ctx.clearRect(0, 0, w, h);

    // Faint horizontal grid
    ctx.strokeStyle = 'rgba(255,255,255,0.06)';
    ctx.lineWidth = 1;
    for (let g = 1; g <= 3; g++) {
      const gy = (h / 4) * g + 0.5;
      ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(w, gy); ctx.stroke();
    }

    // Waveform path (built once for fill, once for stroke)
    let lastX = 0, lastY = 0, firstX = 0, started = false;
    ctx.beginPath();
    for (let i = 0; i < buf.size; i++) {
      const idx = (start + i) % buf.cap;
      const t = buf.times[idx];
      if (t < t0) continue;
      lastX = x(t); lastY = y(buf.values[idx]);
      if (!started) { ctx.moveTo(lastX, lastY); firstX = lastX; started = true; }
      else ctx.lineTo(lastX, lastY);
    }

    const fill = ctx.createLinearGradient(0, 0, 0, h);
    fill.addColorStop(0, color + '33');
    fill.addColorStop(1, color + '00');
    ctx.save();
    ctx.lineTo(lastX, h); ctx.lineTo(firstX, h); ctx.closePath();
    ctx.fillStyle = fill;
    ctx.fill();
    ctx.restore();

    // Break the line across time gaps (missed data) instead of bridging them —
    // a gap wider than GAP_BREAK_MS lifts the pen so old and resumed traces
    // never connect with a false horizontal line.
    const GAP_BREAK_MS = 300;
    ctx.beginPath();
    started = false;
    let prevT = null;
    for (let i = 0; i < buf.size; i++) {
      const idx = (start + i) % buf.cap;
      const t = buf.times[idx];
      if (t < t0) continue;
      const px = x(t), py = y(buf.values[idx]);
      if (!started || (prevT != null && t - prevT > GAP_BREAK_MS)) { ctx.moveTo(px, py); started = true; }
      else ctx.lineTo(px, py);
      prevT = t;
    }
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.lineJoin = 'round';
    ctx.stroke();

    // Emphasised endpoint
    ctx.beginPath();
    ctx.arc(lastX, lastY, 2.5, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
  }
}
